import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import {
  Box, Button, Flex, Icon, Text,
  useColorModeValue, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  useDisclosure, Tabs, TabList, TabPanels, Tab, TabPanel,
  FormControl, FormLabel, Textarea, Grid,
  useColorMode, Badge, Spinner,
  Image,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
  Input,
  VStack,
  HStack,
  Tooltip,
  Editable,
  EditablePreview,
  EditableInput,
  Select,
} from '@chakra-ui/react';

import activosApi from 'api/activos';
import sublineasApi, { Sublinea } from 'api/sublineas';
import hotelesApi from 'api/hoteles';
import departamentosApi from 'api/departamentos';
import tipoAdquisicionApi from 'api/tipoAdquisicion';
import depreciacionApi from 'api/depreciacion';
import { isSoloLectura, getCurrentUser, getDepartamentosPermitidos } from 'utils/permissions';
import Card from 'components/card/Card';
import InputField from 'components/fields/InputField';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';

import {
  MdOutlineInventory,
  MdLaptop, MdTv, MdDevices, MdPhonelink,
  MdComputer, MdBuild, MdPrint,
  MdCloudUpload, MdDescription, MdWarning, MdCheckCircle
} from "react-icons/md";

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface Activo {
  Usuario: string;
  id: number;
  nombre: string;
  numeroSerie: string;
  tipo: string;
  estado: string;
  ubicacion: string;
  URL_Imagen?: string;
  Proveedor?: string;
  Modelo?: string;
  Precio?: string;
  Garantia?: string;
  Precio_depreciado?: string;
  LINEA?: string;
  SUBLINEA?: string;
  PermitidoSalir?: string;
  Folio?: string;
  Status?: string;
  resguardoDepartamento?: string;
  Descripcion?: string;
}

interface Hotel {
  id: string;
  nombre: string;
}

interface TipoSalida {
  id: number;
  nombre: string;
}

interface FormData {
  Usuario: unknown;
  destino: string;
  motivo: string;
  fechaSalida: string;
  fechaRetorno: string;
  responsable: string;
  observaciones: string;
}

/**
 * Mapa directo con los campos de tbaActivos.
 * Campos requeridos: Folio, ActiveName (o al menos uno de los dos como nombre).
 */
interface CSVActivo {
  // Campos obligatorios
  Folio: string;
  ActiveName: string;
  SerialNumber: string;
  Marca: string;               // Requerida para resolver/crear Modelo
  Modelo: string;
  Ubicacion: string;            // Departamento (debe existir tal cual en el catálogo)
  LINEA: string;                // Usada junto con SUBLINEA para resolver SublineaId sin ambigüedad
  SUBLINEA: string;

  // Campos opcionales / relaciones por nombre (el backend resuelve los IDs)
  Proveedor: string;
  Precio: string;
  Garantia: string;           // Meses (ej. "36") o fecha YYYY-MM-DD
  Precio_depreciado: string;
  PermitidoSalir: string;     // "sí"/"si"/"yes"/"1" → true
  Estado: string;              // "Activo"/"active"/etc. Por defecto: Activo
  Usuario?: string;             // Responsable, opcional (FullName exacto)
  TipoAdquisicion?: string;      // "Capex"/"OPEX"/"Arrendamiento", opcional
  RubroDepreciacion?: string;    // Categoria de tbaTablaDepreciacion del año en curso, opcional
  UbicacionExacta?: string;      // Ubicación física específica (piso, habitación, etc.), opcional
  FechaAdquisicion?: string;     // Fecha en que se adquirió el activo (YYYY-MM-DD), opcional
  URL_Imagen?: string;
  Descripcion?: string;

  // Errores de validación detectados en el parseo
  _errors?: string[];
  // Folio previsto (solo informativo, calculado en el frontend); nunca se envía al backend
  _folioPreview?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getDefaultImage = (tipo: string) => {
  switch (tipo?.toLowerCase()) {
    case 'laptop':    return 'https://via.placeholder.com/40x40/3B82F6/FFFFFF?text=💻';
    case 'proyector': return 'https://via.placeholder.com/40x40/10B981/FFFFFF?text=📽';
    case 'impresora': return 'https://via.placeholder.com/40x40/EF4444/FFFFFF?text=🖨';
    case 'tablet':    return 'https://via.placeholder.com/40x40/8B5CF6/FFFFFF?text=📱';
    case 'teléfono':  return 'https://via.placeholder.com/40x40/F97316/FFFFFF?text=📞';
    case 'servidor':  return 'https://via.placeholder.com/40x40/6B7280/FFFFFF?text=🖥';
    default:          return 'https://via.placeholder.com/40x40/9CA3AF/FFFFFF?text=📦';
  }
};

const getEstadoColor = (estado: string) => {
  switch (estado) {
    case 'ACTIVO':    return 'green';
    case 'ASIGNADO':  return 'blue';
    case 'PENDIENTE': return 'yellow';
    case 'PTE. BAJA': return 'yellow';
    case 'baja':      return 'red';
    case 'RMA':       return 'cyan';
    default:          return 'gray';
  }
};

/**
 * Genera un CSV de ejemplo alineado con los campos reales de tbaActivos.
 * El delimitador es TAB para evitar conflictos con comas en nombres.
 */
const crearEjemploCSV = () => {
  const headers = [
    'Folio',
    'ActiveName',
    'SerialNumber',
    'Marca',
    'Modelo',
    'Proveedor',
    'Precio',
    'Garantia',
    'Precio_depreciado',
    'LINEA',
    'SUBLINEA',
    'PermitidoSalir',
    'Estado',
    'Departamento',
    'Usuario',
    'TipoAdquisicion',
    'RubroDepreciacion',
    'UbicacionExacta',
    'FechaAdquisicion',
    'Descripcion',
    'URL_Imagen',
  ].join('\t');

  // Folio se deja en blanco a propósito: se autogenera con el hotel seleccionado
  // en la sesión + la línea de la SUBLINEA elegida + el siguiente consecutivo.
  // Solo inclúyalo si ya trae un folio impreso de antemano (reimportación).
  const filas = [
    ['', 'Laptop Dell XPS 13',      'DELLXPS9310',  'Dell', 'XPS 13 9310',        'Dell México',  '28000', '2025-12-31', '22400', 'TECNOLOGIA DE INFORMACION', 'TI EQ COMPUTO',    'Sí',  'Activo', 'Tecnologias', '', 'Capex', 'EQUIPO DE COMPUTO', 'Piso 2, oficina de sistemas', '2024-03-15', 'Laptop ejecutiva',              'https://m.media-amazon.com/images/I/61LdecwlWYL._AC_SL1500_.jpg'],
    ['', 'Proyector Epson PowerLite','EPSONPL452',   'Epson', 'PowerLite L400U',    'Epson México', '15500', '2026-06-30', '12000', 'TECNOLOGIA DE INFORMACION', 'TI AUDIO Y VIDEO', 'No', 'Activo', 'Banquetes',   '', 'OPEX', 'MOB. Y EQUIPO DE AREAS PUBLICAS', 'Sala de juntas B', '2025-01-10', 'Proyector láser 4K',            ''],
    ['', 'Impresora HP LaserJet Pro','HPLJ4350DN',   'HP', 'LaserJet Pro M404dn', 'HP Inc.',      '8000',  '2024-12-31', '6500',  'TECNOLOGIA DE INFORMACION', 'TI EQ COMPUTO',    'Sí',  'Activo', 'Recursos Humanos', '', 'Arrendamiento', 'MOBILIARIO Y EQUIPO DE ADMON', 'Piso 1, RH', '2025-06-01', 'Impresora monocromática A4',    ''],
  ].map(r => r.join('\t'));

  return [headers, ...filas].join('\n');
};

/**
 * Valida los campos obligatorios de una fila. Se reutiliza tanto al parsear
 * el CSV como al editar una celda en la vista previa.
 */
const validarFila = (activo: Partial<CSVActivo>): string[] => {
  const errors: string[] = [];
  // Folio es opcional: si no se da, el backend lo autogenera con el hotel de la
  // sesión + la línea de la sublínea elegida + el siguiente consecutivo disponible.
  if (!activo.ActiveName)   errors.push('Nombre (ActiveName) requerido');
  if (!activo.SerialNumber) errors.push('Número de serie requerido');
  if (!activo.Marca)        errors.push('Marca requerida');
  if (!activo.Modelo)       errors.push('Modelo requerido');
  if (!activo.Ubicacion)    errors.push('Ubicación/Departamento requerido');
  if (!activo.SUBLINEA)     errors.push('Sublínea requerida');
  return errors;
};

// ─── Parseo CSV ───────────────────────────────────────────────────────────────

const parseCSV = (csvText: string): CSVActivo[] => {
  // Normalizar saltos de línea y filtrar líneas vacías (incluye líneas de puras comas/tabs,
  // como las que exporta Excel/Sheets al inicio de un archivo)
  const lines = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.replace(/[,\t\s]/g, '') !== '');

  if (lines.length < 2) return [];

  // Detectar delimitador
  const firstLine = lines[0];
  const delimiter = firstLine.includes('\t') ? '\t' : ',';

  /**
   * Divide una línea respetando campos entre comillas (para CSV con comas).
   */
  const splitLine = (line: string): string[] => {
    if (delimiter === '\t') {
      return line.split('\t').map(v => v.trim().replace(/^"|"$/g, ''));
    }
    // CSV con comas: respetar comillas
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const rawHeaders = splitLine(lines[0]);
  // Normalizar headers: sin acentos, minúsculas, sin espacios — para que
  // "Tipo de Adquisición", "tipo_adquisicion" y "TipoAdquisicion" (con o sin
  // acentos) se reconozcan todos igual al compararlos contra los alias.
  const quitarAcentos = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const headers = rawHeaders.map(h => quitarAcentos(h).toLowerCase().trim().replace(/\s+/g, '_'));

  /**
   * Normaliza el texto de una celda: cambia espacios "duros" (non-breaking,
   * comunes al copiar/pegar desde Excel) por espacios normales, colapsa
   * espacios internos repetidos a uno solo, y recorta los extremos. Sin esto,
   * dos celdas que se ven idénticas ("Equipo de Computo" vs "Equipo  de
   * Computo") no hacen match contra el catálogo.
   */
  const normalizarTexto = (s: string): string => s.replace(/ /g, ' ').replace(/\s+/g, ' ').trim();

  /**
   * Busca el valor de una columna por múltiples alias posibles.
   */
  const getValue = (values: string[], aliases: string[]): string => {
    for (const alias of aliases) {
      const idx = headers.indexOf(alias.toLowerCase());
      if (idx !== -1 && idx < values.length) return normalizarTexto(values[idx] || '');
    }
    return '';
  };

  const activos: CSVActivo[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = splitLine(lines[i]);
    const errors: string[] = [];

    const folio      = getValue(values, ['folio', 'numero_activo', 'asset_number']);
    const nombre     = getValue(values, ['activename', 'active_name', 'nombre', 'descripcion', 'name', 'descripción']);
    const serie      = getValue(values, ['serialnumber', 'serial_number', 'numeroserie', 'numero_serie', 'serial', 'serie', 'no_serie']);
    const marca      = getValue(values, ['marca', 'brand', 'fabricante']);
    const modelo     = getValue(values, ['modelo', 'model']);
    const proveedor  = getValue(values, ['proveedor', 'vendor', 'supplier']);
    const precio     = getValue(values, ['precio', 'costo', 'valor', 'price', 'cost']);
    const garantia   = getValue(values, ['garantia', 'warranty', 'garantía']);
    const depreciado = getValue(values, ['precio_depreciado', 'depreciado', 'valor_depreciado', 'depreciated']);
    const linea      = getValue(values, ['linea', 'categoria', 'category', 'line', 'línea']);
    const sublinea   = getValue(values, ['sublinea', 'subcategoria', 'subcategory', 'sublínea']);
    const salir      = getValue(values, ['permitidosalir', 'permitido_salir', 'salida_permitida', 'permite_salida', 'allowed_out']);
    const estado     = getValue(values, ['estado', 'status']);
    const ubicacion  = getValue(values, ['ubicacion', 'ubicación', 'location', 'departamento', 'resguardodepartamento']);
    const usuario    = getValue(values, ['usuario', 'responsable', 'user']);
    const tipoAdquisicion = getValue(values, ['tipoadquisicion', 'tipo_adquisicion', 'tipo_de_adquisicion', 'acquisitiontype']);
    const rubroDepreciacion = getValue(values, ['rubrodepreciacion', 'rubro_depreciacion', 'rubro_de_depreciacion', 'categoriadepreciacion', 'categoria_de_depreciacion', 'categoria_depreciacion']);
    const ubicacionExacta = getValue(values, ['ubicacionexacta', 'ubicacion_exacta', 'exactlocation']);
    const fechaAdquisicion = getValue(values, ['fechaadquisicion', 'fecha_adquisicion', 'fecha_de_adquisicion', 'acquisitiondate']);
    const descripcion = getValue(values, ['descripcion', 'descripción', 'description', 'notas', 'notes']);
    const imagen     = getValue(values, ['url_imagen', 'imagen', 'foto', 'image', 'url_image']);

    const activoSinErrores: CSVActivo = {
      Folio:              folio,
      ActiveName:         nombre,
      SerialNumber:       serie,
      Marca:              marca,
      Modelo:             modelo,
      Proveedor:          proveedor,
      Precio:             precio,
      Garantia:           garantia,
      Precio_depreciado:  depreciado,
      LINEA:              linea,
      SUBLINEA:           sublinea,
      PermitidoSalir:     salir,
      Estado:             estado,
      Ubicacion:          ubicacion,
      Usuario:            usuario || undefined,
      TipoAdquisicion:    tipoAdquisicion || undefined,
      RubroDepreciacion:  rubroDepreciacion || undefined,
      UbicacionExacta:    ubicacionExacta || undefined,
      FechaAdquisicion:   fechaAdquisicion || undefined,
      Descripcion:        descripcion,
      URL_Imagen:         imagen || undefined,
    };
    errors.push(...validarFila(activoSinErrores));

    const activo: CSVActivo = {
      ...activoSinErrores,
      _errors: errors.length > 0 ? errors : undefined,
    };

    activos.push(activo);
  }

  return activos;
};

// ─── Componente principal ─────────────────────────────────────────────────────

const ImportarActivos = () => {
  const toast    = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { colorMode } = useColorMode();

  const [csvData, setCsvData]                   = useState<CSVActivo[]>([]);
  const [isImporting, setIsImporting]           = useState(false);
  const [showImportedTable, setShowImportedTable] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen]       = useState(false);
  const [currentTabIndex, setCurrentTabIndex]   = useState(0);
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const [activos, setActivos]               = useState<Activo[]>([]);
  const [selectedActivos, setSelectedActivos] = useState<number[]>([]);
  const [hoteles, setHoteles]               = useState<Hotel[]>([]);
  const [sublineasCatalogo, setSublineasCatalogo] = useState<Sublinea[]>([]);
  const [hotelesCatalogo, setHotelesCatalogo] = useState<{ Id: any; Name: string; Abrev: string }[]>([]);
  const [departamentosCatalogo, setDepartamentosCatalogo] = useState<{ Id: any; Name: string }[]>([]);
  const [tiposAdquisicionCatalogo, setTiposAdquisicionCatalogo] = useState<{ Id: any; Name: string }[]>([]);
  const [rubrosDepreciacionCatalogo, setRubrosDepreciacionCatalogo] = useState<{ Id: any; Categoria: string; Porcentaje: any }[]>([]);
  const [tiposSalida, setTiposSalida]       = useState<TipoSalida[]>([]);
  const [loading, setLoading]               = useState(true);
  const [searchTerm, setSearchTerm]         = useState('');
  const [hotelSeleccionado, setHotelSeleccionado] = useState(
    sessionStorage.getItem('hotelSeleccionado') || ''
  );

  const [formData, setFormData] = useState<FormData>({
    destino: '',
    motivo: '',
    fechaSalida: new Date().toISOString().split('T')[0],
    fechaRetorno: '',
    responsable: '',
    observaciones: '',
    Usuario: '',
  });

  // ── Carga de archivo ──────────────────────────────────────────────────────

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      mostrarError('Por favor, seleccione un archivo CSV válido.');
      return;
    }

    setIsImporting(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const parsedDataSinAcentos = parseCSV(csvText);

        if (parsedDataSinAcentos.length === 0) {
          mostrarError('No se pudieron leer datos del archivo CSV. Verifique el formato.');
          return;
        }

        // LINEA/SUBLINEA: el CSV casi nunca viene con mayúsculas/acentos
        // idénticos al catálogo ("linea de computo" vs "Línea de Cómputo").
        // El backend las empareja sin problema (usa LOWER() con una collation
        // que ya ignora acentos), pero el <Select> del preview compara el
        // valor tal cual, y como no hace match contra ninguna <option> se ve
        // "sin seleccionar" aunque en realidad sí sea válido. Aquí se ajusta
        // el texto a como está escrito en el catálogo para que el preview
        // coincida con lo que de verdad va a pasar al guardar.
        const comparable = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
        const parsedDataCruda = parsedDataSinAcentos.map(item => {
          if (!item.LINEA) return item;
          const lineaCatalogo = sublineasCatalogo.find(s => comparable(s.nom_linea) === comparable(item.LINEA));
          if (!lineaCatalogo) return item;
          const sublineaCatalogo = item.SUBLINEA
            ? sublineasCatalogo.find(
                s => s.nom_linea === lineaCatalogo.nom_linea && comparable(s.nom_sub) === comparable(item.SUBLINEA)
              )
            : undefined;
          return {
            ...item,
            LINEA: lineaCatalogo.nom_linea,
            SUBLINEA: sublineaCatalogo ? sublineaCatalogo.nom_sub : item.SUBLINEA,
          };
        });

        // Si una fila no trae Departamento, se usa por default el del usuario
        // que está importando — no tiene sentido dejarlo en blanco cuando ya
        // sabemos a qué departamento pertenece quien está dando de alta.
        const usuarioActual = getCurrentUser();
        const departamentoDelUsuario = departamentosCatalogo.find(
          (d: any) => String(d.Id) === String(usuarioActual?.DepartamentoId)
        )?.Name;

        const parsedData = departamentoDelUsuario
          ? parsedDataCruda.map(item => {
              if (item.Ubicacion) return item;
              const actualizado = { ...item, Ubicacion: departamentoDelUsuario };
              const errores = validarFila(actualizado);
              return { ...actualizado, _errors: errores.length > 0 ? errores : undefined };
            })
          : parsedDataCruda;

        setCsvData(parsedData);
        setShowImportedTable(true);
        recalcularPreviewFolios(parsedData).then(setCsvData);

        const conErrores = parsedData.filter(r => r._errors?.length).length;
        if (conErrores > 0) {
          toast({
            title: `${parsedData.length} filas cargadas`,
            description: `${conErrores} filas tienen errores de validación (marcadas en rojo). Corrija el CSV antes de guardar.`,
            status: 'warning',
            duration: 7000,
            isClosable: true,
          });
        } else {
          mostrarExito(`Se cargaron ${parsedData.length} activos listos para importar.`);
        }
      } catch (error) {
        console.error('Error al procesar CSV:', error);
        mostrarError('Error al procesar el archivo CSV. Verifique el formato.');
      } finally {
        setIsImporting(false);
      }
    };

    reader.onerror = () => {
      mostrarError('Error al leer el archivo.');
      setIsImporting(false);
    };

    reader.readAsText(file, 'UTF-8');
  };

  // ── Guardar importación ───────────────────────────────────────────────────

  const handleGuardarImportados = async () => {
    if (csvData.length === 0) {
      mostrarError('No hay datos para guardar.');
      return;
    }

    const conErrores = csvData.filter(r => r._errors?.length).length;
    if (conErrores > 0) {
      mostrarError(`Hay ${conErrores} filas con errores de validación. Corrija el CSV antes de importar.`);
      return;
    }

    try {
      setIsImporting(true);

      // Los nombres de campo deben coincidir con lo que resuelve
      // insertActivosImportados (Departamento, Sublinea+Linea, Marca, Modelo,
      // Proveedor y Estado se resuelven/crean por nombre en el backend).
      const activosPayload = csvData.map(item => ({
        Folio:              item.Folio,
        ActiveName:         item.ActiveName,
        SerialNumber:       item.SerialNumber,
        Marca:              item.Marca,
        Modelo:             item.Modelo,
        Proveedor:          item.Proveedor || null,
        Precio:             item.Precio || null,
        Garantia:           item.Garantia || null,
        Precio_depreciado:  item.Precio_depreciado || null,
        LINEA:              item.LINEA,
        SUBLINEA:           item.SUBLINEA,
        PermitidoSalir:     ['sí', 'si', 'yes', '1', 'true'].includes(
                              item.PermitidoSalir?.toLowerCase() || ''
                            ) ? 1 : 0,
        Estado:             item.Estado || 'Activo',
        Ubicacion:          item.Ubicacion,
        Usuario:            item.Usuario || null,
        TipoAdquisicion:    item.TipoAdquisicion || null,
        RubroDepreciacion:  item.RubroDepreciacion || null,
        UbicacionExacta:    item.UbicacionExacta || null,
        FechaAdquisicion:   item.FechaAdquisicion || null,
        Descripcion:        item.Descripcion || null,
      }));

      const response = await fetch(
        'https://inventariosbackend.velasresorts.com/inventarios/activos/importar',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ activos: activosPayload, hotelSeleccionado }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        if (Array.isArray(result.errores) && result.errores.length > 0) {
          // Marca en la tabla de vista previa las filas que el backend rechazo
          const erroresPorFila = new Map<number, string[]>(
            result.errores.map((e: { fila: number; error: string }) => [e.fila, e.error.split('; ')])
          );
          setCsvData(prev => prev.map((item, i) =>
            erroresPorFila.has(i + 1) ? { ...item, _errors: erroresPorFila.get(i + 1) } : item
          ));
          mostrarError(`Se encontraron ${result.errores.length} error(es). Revisa las filas marcadas en rojo.`);
        } else {
          throw new Error(result.error || result.mensaje || `Error ${response.status}`);
        }
        return;
      }

      mostrarExito(result.mensaje || `${result.importados} activos importados correctamente.`);

      setShowImportedTable(false);
      setCsvData([]);
      const fileInput = document.getElementById('csv-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      // Recargar lista
      cargarDatosIniciales();
    } catch (error) {
      console.error('Error al guardar activos importados:', error);
      mostrarError('Error al guardar los activos importados. Revisa la consola para más detalles.');
    } finally {
      setIsImporting(false);
    }
  };

  // ── Edición inline de la vista previa ─────────────────────────────────────

  // Cache de consecutivo actual por "LINEA::SUBLINEA" (solo lectura, no reserva nada).
  const previewCacheRef = useRef<Map<string, { codigoHotelFolio: string; numLinea: string; consecutivoActual: number }>>(new Map());

  /**
   * Recalcula el folio PREVISTO (_folioPreview) de cada fila sin Folio explícito,
   * consultando el consecutivo actual por cada combinación LINEA+SUBLINEA presente
   * y sumando el desplazamiento (offset) de cada fila dentro de su propio grupo,
   * para que se vean consecutivos entre sí aunque compartan línea.
   * No reserva ni modifica nada en el servidor — es solo una previsualización;
   * el valor definitivo se calcula de nuevo, de forma atómica, al guardar.
   */
  const recalcularPreviewFolios = async (data: CSVActivo[]): Promise<CSVActivo[]> => {
    if (!hotelSeleccionado) return data.map((row): CSVActivo => ({ ...row, _folioPreview: undefined }));

    const combosFaltantes = new Set<string>();
    data.forEach(row => {
      if (!row.Folio && row.LINEA && row.SUBLINEA) {
        const key = `${row.LINEA}::${row.SUBLINEA}`;
        const cacheado = previewCacheRef.current.get(key);
        // Si ya hay algo cacheado pero le falta un campo (ej. quedó de una versión
        // anterior de este código en memoria), se vuelve a pedir en vez de usarlo tal cual.
        if (!cacheado || !cacheado.codigoHotelFolio || !cacheado.numLinea) {
          combosFaltantes.add(key);
        }
      }
    });

    await Promise.all(
      Array.from(combosFaltantes).map(async (key) => {
        const [linea, sublinea] = key.split('::');
        try {
          const resp = await fetch(
            `https://inventariosbackend.velasresorts.com/inventarios/activos/previsualizar-folio?hotel=${encodeURIComponent(hotelSeleccionado)}&linea=${encodeURIComponent(linea)}&sublinea=${encodeURIComponent(sublinea)}`
          );
          const json = await resp.json();
          if (json.success) {
            previewCacheRef.current.set(key, {
              codigoHotelFolio: json.codigoHotelFolio,
              numLinea: json.numLinea,
              consecutivoActual: json.consecutivoActual,
            });
          }
        } catch {
          // Sin conexión o error de red: simplemente no se muestra preview para ese grupo.
        }
      })
    );

    const offsetsPorGrupo = new Map<string, number>();
    return data.map((row): CSVActivo => {
      if (row.Folio || !row.LINEA || !row.SUBLINEA) {
        return { ...row, _folioPreview: undefined };
      }
      const key = `${row.LINEA}::${row.SUBLINEA}`;
      const cache = previewCacheRef.current.get(key);
      if (!cache || !cache.codigoHotelFolio || !cache.numLinea) return { ...row, _folioPreview: undefined };
      const offset = (offsetsPorGrupo.get(key) || 0) + 1;
      offsetsPorGrupo.set(key, offset);
      const folioPreview = `${cache.codigoHotelFolio}-${cache.numLinea}-${String(cache.consecutivoActual + offset).padStart(6, '0')}`;
      return { ...row, _folioPreview: folioPreview };
    });
  };

  const handleEditarCampo = (index: number, campo: keyof CSVActivo, valor: string) => {
    setCsvData(prev => {
      const nuevo = [...prev];
      const fila: CSVActivo = { ...nuevo[index], [campo]: valor };
      const errores = validarFila(fila);
      fila._errors = errores.length > 0 ? errores : undefined;
      nuevo[index] = fila;
      if (campo === 'SUBLINEA' || campo === 'Folio') {
        recalcularPreviewFolios(nuevo).then(setCsvData);
      }
      return nuevo;
    });
  };

  // Al cambiar LINEA, si la SUBLINEA actual ya no pertenece a esa línea, se limpia
  const handleCambiarLinea = (index: number, valor: string) => {
    setCsvData(prev => {
      const nuevo = [...prev];
      const actual = nuevo[index];
      const sublineaSigueValida = sublineasCatalogo.some(
        s => s.nom_linea === valor && s.nom_sub === actual.SUBLINEA
      );
      const fila: CSVActivo = { ...actual, LINEA: valor, SUBLINEA: sublineaSigueValida ? actual.SUBLINEA : '' };
      const errores = validarFila(fila);
      fila._errors = errores.length > 0 ? errores : undefined;
      nuevo[index] = fila;
      recalcularPreviewFolios(nuevo).then(setCsvData);
      return nuevo;
    });
  };

  // ── Cancelar importación ──────────────────────────────────────────────────

  const handleCancelarImportacion = () => {
    setCsvData([]);
    setShowImportedTable(false);
    const fileInput = document.getElementById('csv-file') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  // ── Datos iniciales ───────────────────────────────────────────────────────

  const cargarDatosIniciales = async () => {
    try {
      setLoading(true);
      const activosResponse = await activosApi.listado();
      const activosData = activosResponse.data || activosResponse;

      // Resuelto del catálogo real de hoteles, no hardcodeado — así no hay que
      // tocar código si se agrega, renombra o reordena un hotel.
      const hotelMap: Record<string, number> = {};
      hotelesCatalogo.forEach((h: any) => { if (h.Abrev) hotelMap[h.Abrev] = h.Id; });
      const hotelId = hotelMap[sessionStorage.getItem('hotelSeleccionado') || ''] || null;

      const activosFormateados = activosData
        .filter((a: any) => !hotelId || a.HotelId === hotelId)
        .map((a: any) => ({
          id:           a.Id || a.id || a._id,
          nombre:       a.ActiveName || a.modelo || 'Activo sin nombre',
          numeroSerie:  a.SerialNumber || 'N/A',
          tipo:         a.tipo || 'equipo',
          estado:       a.Estado === 'active'        ? 'ACTIVO'
                      : a.Estado === 'assigned'      ? 'ASIGNADO'
                      : a.Estado === 'pending'        ? 'PENDIENTE'
                      : a.Estado === 'pending_baja'   ? 'PTE. BAJA'
                      : a.Estado === 'inactive'       ? 'INACTIVO'
                      : a.Estado === 'under_warranty' ? 'EN GARANTÍA'
                      : a.Estado === 'in_maintenance' ? 'EN MANTENIMIENTO'
                      : a.Estado === 'in_repair'      ? 'EN REPARACIÓN'
                      : a.Estado === 'rma'            ? 'RMA'
                      : 'INACTIVO',
          ubicacion:   a.ubicacion || a.departamento || 'Sin especificar',
          URL_Imagen:  a.URL_Imagen || null,
          HotelId:     a.HotelId,
          Usuario:     a.Usuario || 'Desconocido',
        }));

      setActivos(activosFormateados);
      setHoteles([
        { id: 'RN',  nombre: 'Resort Nuevo Vallarta' },
        { id: 'RM',  nombre: 'Resort Mazatlán' },
        { id: 'LC',  nombre: 'Resort Los Cabos' },
        { id: 'VV',  nombre: 'Resort Vallarta' },
      ]);
      setTiposSalida([
        { id: 1, nombre: 'Baja Definitiva' },
        { id: 2, nombre: 'Salida Temporal' },
        { id: 3, nombre: 'Transferencia' },
      ]);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      mostrarError('Error al cargar los activos. Por favor, intente nuevamente.');
      setActivos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargarDatosIniciales(); }, []);

  useEffect(() => {
    sublineasApi.listado().then(setSublineasCatalogo);
    hotelesApi.listado().then((data: any) => setHotelesCatalogo(Array.isArray(data) ? data : []));
    departamentosApi.listado().then((data: any) => {
      const lista = Array.isArray(data) ? data : [];
      // Un usuario sin VeTodosDepartamentos (p.ej. rol Usuario) solo puede
      // importar activos a su propio departamento (o a los adicionales que
      // se le hayan asignado explícitamente) — no a cualquier departamento
      // del sistema. Mismo mecanismo que ya restringe la tabla de activos.
      const permitidos = getDepartamentosPermitidos();
      const filtrada = permitidos === null
        ? lista
        : lista.filter((d: any) => permitidos.includes(Number(d.Id)));
      setDepartamentosCatalogo(filtrada);
    });
    tipoAdquisicionApi.listado().then((data: any) => setTiposAdquisicionCatalogo(Array.isArray(data) ? data : []));
    // Simplificación: se usa la tabla de depreciación del año en curso para
    // todas las filas, igual que el alta manual antes de tener una fecha de
    // adquisición capturada. Si una fila trae una fecha de adquisición de
    // otro año, su Rubro de depreciación se sigue mandando por nombre al
    // backend (RubroDepreciacion es un string, no un Id), así que aunque el
    // select aquí muestre las opciones del año en curso, seguirá resolviendo
    // bien mientras el nombre del rubro exista también en la tabla de ese año.
    depreciacionApi.listado(new Date().getFullYear()).then((data: any) =>
      setRubrosDepreciacionCatalogo(Array.isArray(data) ? data : [])
    );
  }, []);

  const ESTADOS_IMPORTACION = ['Activo', 'Bodega', 'RMA'];

  const lineasDisponibles = Array.from(new Set(sublineasCatalogo.map(s => s.nom_linea))).sort();
  const sublineasPorLinea = (nomLinea: string) =>
    Array.from(new Set(sublineasCatalogo.filter(s => s.nom_linea === nomLinea).map(s => s.nom_sub))).sort();

  useEffect(() => {
    try {
      const userData = sessionStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        setFormData(prev => ({ ...prev, responsable: user.FullName || '' }));
      }
    } catch {}
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const current = sessionStorage.getItem('hotelSeleccionado') || '';
      if (current !== hotelSeleccionado) {
        setHotelSeleccionado(current);
        cargarDatosIniciales();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [hotelSeleccionado]);

  // ── Helpers UI ────────────────────────────────────────────────────────────

  const mostrarError = (msg: string) =>
    toast({ title: 'Error', description: msg, status: 'error', duration: 5000, isClosable: true });

  const mostrarExito = (msg: string) =>
    toast({ title: 'Éxito', description: msg, status: 'success', duration: 5000, isClosable: true });

  // ── Tema ──────────────────────────────────────────────────────────────────

  const borderColor         = useColorModeValue('gray.200', 'whiteAlpha.300');
  const cardBg              = useColorModeValue('white', 'navy.800');
  const textColor           = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary  = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const rowHoverBg          = useColorModeValue('gray.50', 'whiteAlpha.50');
  const errorRowBg          = useColorModeValue('red.50', 'red.900');

  // Resumen de validación
  const totalFilas   = csvData.length;
  const filasOk      = csvData.filter(r => !r._errors?.length).length;
  const filasError   = totalFilas - filasOk;

  if (isSoloLectura()) {
    return <Navigate to="/admin/main/ecommerce/order-list" replace />;
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} p={4}>

      {/* ── Sección upload ──────────────────────────────────────────────── */}
      <Card mb={6} p={4}>
        <VStack spacing={4} align="stretch">
          <Flex alignItems="center">
            <Icon as={MdCloudUpload} w={6} h={6} color="blue.500" mr={2} />
            <Text fontSize="lg" fontWeight="bold">Importar Activos desde CSV</Text>
          </Flex>

          <Text color={textColorSecondary} fontSize="sm">
            Suba un archivo CSV (separado por <strong>tabs</strong> o comas) con las columnas obligatorias:
            {' '}
            <code>ActiveName</code>, <code>SerialNumber</code>, <code>Marca</code>,
            <code>Modelo</code>, <code>LINEA</code>, <code>SUBLINEA</code>, <code>Ubicacion</code>
            {' '}(<strong>Departamento</strong> del activo — debe coincidir exacto con uno existente); y opcionales:
            {' '}<code>Folio</code>, <code>Proveedor</code>, <code>Precio</code>, <code>Garantia</code>,
            <code>Precio_depreciado</code>, <code>PermitidoSalir</code>, <code>Estado</code>,
            <code>Usuario</code>, <code>TipoAdquisicion</code> (Capex/OPEX/Arrendamiento),
            <code>RubroDepreciacion</code> (debe coincidir con un rubro de la Tabla de Depreciación del año en curso),
            <code>UbicacionExacta</code> (ubicación física, distinta del Departamento — piso, habitación, etc.),
            <code>FechaAdquisicion</code> (YYYY-MM-DD),
            <code>Descripcion</code>, <code>URL_Imagen</code>.
            {' '}Marca y Proveedor se crean automáticamente si no existen; LINEA+SUBLINEA deben
            corresponder a una combinación real ya existente en el catálogo.
            {' '}<strong>Folio es opcional</strong>: si no lo trae, se autogenera con el
            {' '}<strong>hotel seleccionado en este momento ({hotelSeleccionado || 'ninguno'})</strong>,
            {' '}la línea de la SUBLINEA elegida y el siguiente consecutivo disponible — si no hay un
            hotel seleccionado, esas filas quedarán marcadas con error.
          </Text>

          <HStack spacing={4}>
            <Box flex={1}>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                disabled={isImporting}
              />
            </Box>
            <Button
              leftIcon={<Icon as={MdDescription} />}
              colorScheme="blue"
              variant="outline"
              onClick={() => {
                const blob = new Blob([crearEjemploCSV()], { type: 'text/csv' });
                const url  = URL.createObjectURL(blob);
                const a    = document.createElement('a');
                a.href     = url;
                a.download = 'ejemplo_activos.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Descargar Ejemplo
            </Button>
          </HStack>

          {isImporting && (
            <Flex align="center" justify="center" p={4}>
              <Spinner size="sm" mr={2} />
              <Text>Procesando archivo...</Text>
            </Flex>
          )}
        </VStack>
      </Card>

      {/* ── Vista previa ────────────────────────────────────────────────── */}
      {showImportedTable && csvData.length > 0 && (
        <Card mb={6} p={4}>
          <VStack spacing={4} align="stretch">

            {/* Cabecera */}
            <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <VStack align="start" spacing={0}>
                <Text fontSize="lg" fontWeight="bold">
                  Vista Previa — {totalFilas} fila{totalFilas !== 1 ? 's' : ''}
                </Text>
                <HStack spacing={3}>
                  <HStack spacing={1}>
                    <Icon as={MdCheckCircle} color="green.500" />
                    <Text fontSize="sm" color="green.600">{filasOk} listas</Text>
                  </HStack>
                  {filasError > 0 && (
                    <HStack spacing={1}>
                      <Icon as={MdWarning} color="red.500" />
                      <Text fontSize="sm" color="red.500">{filasError} con errores</Text>
                    </HStack>
                  )}
                </HStack>
              </VStack>

              <HStack>
                <Button size="sm" colorScheme="red" variant="outline" onClick={handleCancelarImportacion}>
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={handleGuardarImportados}
                  isLoading={isImporting}
                  isDisabled={filasError > 0}
                  title={filasError > 0 ? 'Corrija los errores antes de guardar' : ''}
                >
                  Confirmar y Guardar
                </Button>
              </HStack>
            </Flex>

            {filasError > 0 && (
              <Box borderWidth="1px" borderColor="red.300" borderRadius="md" bg={errorRowBg} p={3}>
                <Text fontSize="xs" fontWeight="bold" color="red.500" mb={2}>
                  ⚠ {filasError} fila{filasError !== 1 ? 's' : ''} con error — corrígelas en el CSV y vuelve a cargarlo:
                </Text>
                <VStack align="stretch" spacing={1} maxH="180px" overflowY="auto">
                  {csvData.map((row, index) => {
                    if (!row._errors?.length) return null;
                    return (
                      <Text key={index} fontSize="xs" color={textColor}>
                        <Text as="span" fontWeight="bold">Fila {index + 1}</Text>
                        {row.Folio ? ` (Folio ${row.Folio})` : ''}: {row._errors.join('; ')}
                      </Text>
                    );
                  })}
                </VStack>
              </Box>
            )}

            {/* Tabla */}
            <Box maxH="500px" overflow="auto">

              {/* Header */}
              <Grid
                templateColumns="28px 28px 1fr 1fr 1fr 1fr 1fr 1fr 80px 90px 80px 90px 90px 90px 1fr 130px 160px 120px 90px"
                gap={2} px={3} py={2}
                fontWeight="bold" fontSize="xs"
                borderBottom="1px solid" borderColor={borderColor}
                alignItems="center"
                minWidth="1800px"
                position="sticky" top={0} bg={cardBg} zIndex={1}
              >
                <Box></Box>  {/* imagen */}
                <Box></Box>  {/* estado validación */}
                <Box>Folio</Box>
                <Box>Nombre</Box>
                <Box>Núm. Serie</Box>
                <Box>Marca</Box>
                <Box>Modelo</Box>
                <Box>Proveedor</Box>
                <Box>Precio</Box>
                <Box>Garantía</Box>
                <Box>Precio Dep.</Box>
                <Box>Línea</Box>
                <Box>SubLínea</Box>
                <Box>Permite Salir</Box>
                <Box>Departamento</Box>
                <Box>Tipo Adquisición</Box>
                <Box>Rubro Depreciación</Box>
                <Box>Fecha Adquisición</Box>
                <Box>Estado</Box>
              </Grid>

              {/* Filas */}
              {csvData.map((activo, index) => {
                const tieneError = (activo._errors?.length ?? 0) > 0;
                return (
                  <Grid
                    key={index}
                    templateColumns="28px 28px 1fr 1fr 1fr 1fr 1fr 1fr 80px 90px 80px 90px 90px 90px 1fr 130px 160px 120px 90px"
                    gap={2} px={3} py={2}
                    alignItems="center"
                    borderBottom="1px solid" borderColor={borderColor}
                    _hover={{ bg: tieneError ? 'red.100' : rowHoverBg }}
                    bg={tieneError ? errorRowBg : undefined}
                    fontSize="xs"
                    minWidth="1800px"
                  >
                    {/* Imagen */}
                    <Box>
                      <Image
                        src={activo.URL_Imagen || getDefaultImage(activo.LINEA)}
                        alt={activo.ActiveName}
                        w="24px" h="24px"
                        borderRadius="full"
                        objectFit="cover"
                        fallbackSrc={getDefaultImage(activo.LINEA)}
                      />
                    </Box>

                    {/* Indicador de validación */}
                    <Box>
                      {tieneError ? (
                        <Tooltip label={activo._errors?.join(', ')} placement="right">
                          <span>
                            <Icon as={MdWarning} color="red.500" w={4} h={4} cursor="help" />
                          </span>
                        </Tooltip>
                      ) : (
                        <Icon as={MdCheckCircle} color="green.400" w={4} h={4} />
                      )}
                    </Box>

                    <Box>
                      <Editable
                        value={activo.Folio || ''}
                        onChange={(val) => handleEditarCampo(index, 'Folio', val)}
                        placeholder="(automático)"
                        fontSize="xs"
                      >
                        <EditablePreview />
                        <EditableInput />
                      </Editable>
                      {!activo.Folio && (
                        <Text fontSize="10px" color={textColorSecondary} fontStyle="italic" isTruncated>
                          {activo._folioPreview ? `→ ${activo._folioPreview}` : (hotelSeleccionado ? '...' : 'seleccione un hotel')}
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Editable
                        value={activo.ActiveName || ''}
                        onChange={(val) => handleEditarCampo(index, 'ActiveName', val)}
                        fontSize="xs"
                        fontWeight="medium"
                        color={!activo.ActiveName ? 'red.500' : undefined}
                      >
                        <EditablePreview />
                        <EditableInput />
                      </Editable>
                    </Box>
                    <Box>
                      <Editable
                        value={activo.SerialNumber || ''}
                        onChange={(val) => handleEditarCampo(index, 'SerialNumber', val)}
                        fontSize="xs"
                        color={!activo.SerialNumber ? 'red.500' : textColorSecondary}
                      >
                        <EditablePreview />
                        <EditableInput />
                      </Editable>
                    </Box>
                    <Box>
                      <Editable
                        value={activo.Marca || ''}
                        onChange={(val) => handleEditarCampo(index, 'Marca', val)}
                        fontSize="xs"
                        color={!activo.Marca ? 'red.500' : undefined}
                      >
                        <EditablePreview />
                        <EditableInput />
                      </Editable>
                    </Box>
                    <Box>
                      <Editable
                        value={activo.Modelo || ''}
                        onChange={(val) => handleEditarCampo(index, 'Modelo', val)}
                        fontSize="xs"
                        color={!activo.Modelo ? 'red.500' : undefined}
                      >
                        <EditablePreview />
                        <EditableInput />
                      </Editable>
                    </Box>
                    <Box>
                      <Editable
                        value={activo.Proveedor || ''}
                        onChange={(val) => handleEditarCampo(index, 'Proveedor', val)}
                        fontSize="xs"
                      >
                        <EditablePreview />
                        <EditableInput />
                      </Editable>
                    </Box>
                    <Box>
                      <Editable
                        value={activo.Precio || ''}
                        onChange={(val) => handleEditarCampo(index, 'Precio', val)}
                        fontSize="xs"
                      >
                        <EditablePreview />
                        <EditableInput />
                      </Editable>
                    </Box>
                    <Box>
                      <Editable
                        value={activo.Garantia || ''}
                        onChange={(val) => handleEditarCampo(index, 'Garantia', val)}
                        fontSize="xs"
                      >
                        <EditablePreview />
                        <EditableInput />
                      </Editable>
                    </Box>
                    <Box>
                      <Editable
                        value={activo.Precio_depreciado || ''}
                        onChange={(val) => handleEditarCampo(index, 'Precio_depreciado', val)}
                        fontSize="xs"
                      >
                        <EditablePreview />
                        <EditableInput />
                      </Editable>
                    </Box>
                    <Box>
                      <Select
                        size="xs"
                        value={activo.LINEA || ''}
                        onChange={(e) => handleCambiarLinea(index, e.target.value)}
                        borderColor={!activo.LINEA ? 'red.300' : undefined}
                      >
                        <option value="">— Seleccione —</option>
                        {lineasDisponibles.map(l => <option key={l} value={l}>{l}</option>)}
                      </Select>
                    </Box>
                    <Box>
                      <Select
                        size="xs"
                        value={activo.SUBLINEA || ''}
                        onChange={(e) => handleEditarCampo(index, 'SUBLINEA', e.target.value)}
                        borderColor={!activo.SUBLINEA ? 'red.300' : undefined}
                        isDisabled={!activo.LINEA}
                      >
                        <option value="">— Seleccione —</option>
                        {sublineasPorLinea(activo.LINEA || '').map(s => <option key={s} value={s}>{s}</option>)}
                      </Select>
                    </Box>
                    <Box>
                      <Badge
                        cursor="pointer"
                        onClick={() => handleEditarCampo(
                          index,
                          'PermitidoSalir',
                          ['sí', 'si', 'yes', '1', 'true'].includes(activo.PermitidoSalir?.toLowerCase() || '') ? 'No' : 'Sí'
                        )}
                        colorScheme={
                          ['sí', 'si', 'yes', '1', 'true'].includes(
                            activo.PermitidoSalir?.toLowerCase() || ''
                          ) ? 'green' : 'red'
                        }
                        fontSize="xs"
                        title="Click para cambiar"
                      >
                        {activo.PermitidoSalir || 'No'}
                      </Badge>
                    </Box>
                    <Box>
                      <Select
                        size="xs"
                        value={activo.Ubicacion || ''}
                        onChange={(e) => handleEditarCampo(index, 'Ubicacion', e.target.value)}
                        borderColor={!activo.Ubicacion ? 'red.300' : undefined}
                      >
                        <option value="">— Seleccione —</option>
                        {departamentosCatalogo.map(d => (
                          <option key={d.Id} value={d.Name}>{d.Name}</option>
                        ))}
                      </Select>
                    </Box>
                    <Box>
                      <Select
                        size="xs"
                        value={activo.TipoAdquisicion || ''}
                        onChange={(e) => handleEditarCampo(index, 'TipoAdquisicion', e.target.value)}
                      >
                        <option value="">— Opcional —</option>
                        {tiposAdquisicionCatalogo.map(t => (
                          <option key={t.Id} value={t.Name}>{t.Name}</option>
                        ))}
                      </Select>
                    </Box>
                    <Box>
                      <Select
                        size="xs"
                        value={activo.RubroDepreciacion || ''}
                        onChange={(e) => handleEditarCampo(index, 'RubroDepreciacion', e.target.value)}
                        isDisabled={rubrosDepreciacionCatalogo.length === 0}
                      >
                        <option value="">— Opcional —</option>
                        {rubrosDepreciacionCatalogo.map(r => (
                          <option key={r.Id} value={r.Categoria}>{r.Categoria} ({r.Porcentaje}%)</option>
                        ))}
                      </Select>
                    </Box>
                    <Box>
                      <Input
                        size="xs"
                        type="date"
                        value={activo.FechaAdquisicion || ''}
                        onChange={(e) => handleEditarCampo(index, 'FechaAdquisicion', e.target.value)}
                      />
                    </Box>
                    <Box>
                      <Select
                        size="xs"
                        value={activo.Estado || 'Activo'}
                        onChange={(e) => handleEditarCampo(index, 'Estado', e.target.value)}
                      >
                        {ESTADOS_IMPORTACION.map(e => (
                          <option key={e} value={e}>{e}</option>
                        ))}
                      </Select>
                    </Box>
                  </Grid>
                );
              })}
            </Box>
          </VStack>
        </Card>
      )}
    </Box>
  );
};

export default ImportarActivos;